import { useCallback, useMemo } from 'react';

import type { MinimalDevice } from '@rtkwlf/asset-data-service';
import get from 'lodash/get';

import { useFeatureFlag } from '@packages/platform/feature-flags/use-feature-flag';

import {
  CSV_COLUMN_FIELD_ACCESSOR,
  DEFAULT_COLUMNS,
  RISK_ADDITIONS_COLUMNS,
  OPTIONAL_COLUMNS,
  ADS_FULL_MOCKED_COLUMNS,
} from './assets-export-schema-config';

const DEFAULT_FIELD_VALUE = '';

/**
 * This hook provides schema utilities for exporting asset data to CSV format.
 *
 * @returns {Object} An object containing the following functions:
 *   - getCSVFields: Returns an array of column fields to be included in the CSV export, based on feature flag configurations.
 *   - getCSVRowData: Transforms a MinimalDevice object into an array of values corresponding to the CSV fields.
 */

export const useAssetsExportSchema = () => {
  const [fullMockedFF] = useFeatureFlag('ADS_FULL_MOCKED');
  const [isFenrirV2FeatureEnabled] = useFeatureFlag('ADS_FENRIR_V2');
  const [riskAdditionsFF] = useFeatureFlag('ADS_RISK_ADDITIONS');

  const csvFields = useMemo(() => {
    const fields = Array.from(DEFAULT_COLUMNS);
    if (riskAdditionsFF) {
      fields.push(...RISK_ADDITIONS_COLUMNS);
    }
    if (isFenrirV2FeatureEnabled) {
      fields.push(...OPTIONAL_COLUMNS);
    }
    if (fullMockedFF) {
      fields.push(...ADS_FULL_MOCKED_COLUMNS);
    }
    return fields;
  }, [fullMockedFF, isFenrirV2FeatureEnabled, riskAdditionsFF]);

  const getCSVFields = useCallback(() => csvFields, [csvFields]);

  const getCSVRowData = useCallback(
    (row: MinimalDevice) => {
      const rowData = csvFields.map((field) => {
        const accessor = CSV_COLUMN_FIELD_ACCESSOR[field];
        if (typeof accessor === 'function') {
          return accessor(row);
        }
        return get(row, accessor, DEFAULT_FIELD_VALUE);
      });

      return rowData;
    },
    [csvFields],
  );

  return { getCSVFields, getCSVRowData };
};
